// src/components/exam-player/renderers/WordBankRenderer.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

/* ---------------- Helpers ---------------- */

function parseStyleString(str = "") {
  const out = {};
  str.split(";").forEach((decl) => {
    const [rawProp, ...rest] = decl.split(":");
    if (!rawProp || !rest.length) return;
    const prop = rawProp.trim();
    const value = rest.join(":").trim();
    if (!prop) return;
    const camel = prop.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
    out[camel] = value;
  });
  return out;
}

/**
 * HTML را پارس می‌کند و هر `[]` را تبدیل می‌کند به کنترل جای‌خالی که
 * توسط renderBlank(blankKey, reactKey) رندر می‌شود.
 * ترتیب [] ها به ترتیب blankDefs مپ می‌شود.
 */
function renderHtmlWithWordBank(html, blankDefs, renderBlank) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html || "", "text/html");
  const rxBlank = /\[\s*\]/g;
  let blankIndex = 0;

  const toReact = (node, key) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || "";
      const parts = [];
      let lastIndex = 0;
      let m;

      while ((m = rxBlank.exec(text))) {
        const before = text.slice(lastIndex, m.index);
        if (before) parts.push(before);

        const def = blankIndex < blankDefs.length ? blankDefs[blankIndex] : null;
        const blankKey = def
          ? String(def.key || blankIndex + 1)
          : String(blankIndex + 1);

        blankIndex += 1;

        parts.push(
          renderBlank(blankKey, `${key}-blank-${m.index}-${blankIndex}`)
        );

        lastIndex = rxBlank.lastIndex;
      }

      const tail = text.slice(lastIndex);
      if (tail) parts.push(tail);

      return parts;
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = node.tagName.toLowerCase();
      const props = { key };

      for (const attr of node.attributes) {
        const name = attr.name;
        const value = attr.value;
        if (name === "class") props.className = value;
        else if (name === "style") props.style = parseStyleString(value);
        else if (name.startsWith("data-")) props[name] = value;
        else {
          const map = {
            colspan: "colSpan",
            rowspan: "rowSpan",
            cellpadding: "cellPadding",
            cellspacing: "cellSpacing",
            for: "htmlFor",
          };
          const reactName = map[name] || name;
          props[reactName] = value;
        }
      }

      const children = [];
      let i = 0;
      for (let child = node.firstChild; child; child = child.nextSibling) {
        children.push(toReact(child, `${key}-${i++}`));
      }
      return React.createElement(tag, props, ...children);
    }

    return null;
  };

  const children = [];
  let i = 0;
  for (let child = doc.body.firstChild; child; child = child.nextSibling) {
    children.push(toReact(child, `root-${i++}`));
  }
  return children;
}

/* ---------------- Main Renderer ---------------- */

export default function WordBankRenderer({
  item,
  onAnswerChange,
  initialAnswers,
  questionNumber,
  hideStem = false,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");

  const stemHtml = item?.stem?.fa || item?.stem?.en || "";
  const textHtml = item?.body?.text?.fa || item?.body?.text?.en || "";
  const layout = item?.body?.layout || "top-bottom";

  const blanksRaw = Array.isArray(item?.body?.blanks) ? item.body.blanks : [];
  const bankRaw = Array.isArray(item?.body?.bank) ? item.body.bank : [];

  // تعداد [] در متن
  const placeholdersCount = useMemo(() => {
    if (!textHtml) return 0;
    const rx = /\[\s*\]/g;
    let c = 0;
    let m;
    while ((m = rx.exec(textHtml))) c++;
    return c;
  }, [textHtml]);

  /**
   * blankDefs: آرایه‌ای هم‌اندازه با تعداد جای‌خالی‌ها
   * برای هر index: از blanksRaw[i] استفاده می‌کنیم، اگر نبود خودمان key = i+1 می‌سازیم.
   */
  const blankDefs = useMemo(() => {
    const count =
      placeholdersCount > 0 ? placeholdersCount : Math.max(blanksRaw.length, 0);
    const result = [];
    for (let i = 0; i < count; i++) {
      const def = blanksRaw[i];
      const key = def && def.key ? String(def.key) : String(i + 1);
      const answers = Array.isArray(def?.answers) ? def.answers : [];
      result.push({ key, answers });
    }
    return result;
  }, [blanksRaw, placeholdersCount]);

  const blankKeys = useMemo(
    () => blankDefs.map((b) => String(b.key)),
    [blankDefs]
  );

  // بانک کلمات: key -> label
  const bankOptions = useMemo(
    () =>
      bankRaw.map((w, idx) => ({
        key: w.key || `w${idx + 1}`,
        label:
          w?.label?.fa?.trim?.() ||
          w?.label?.en?.trim?.() ||
          t("word_bank.word_default", { n: idx + 1 }),
      })),
    [bankRaw, t]
  );
  const bankMap = useMemo(() => {
    const m = new Map();
    bankOptions.forEach((w) => m.set(w.key, w.label));
    return m;
  }, [bankOptions]);

  // state: جواب‌های فعلی (blankKey -> bankKey)
  const [answers, setAnswers] = useState(() => {
    const base = {};
    blankKeys.forEach((k) => {
      const fromInit =
        initialAnswers && typeof initialAnswers[k] === "string"
          ? initialAnswers[k]
          : "";
      base[k] = fromInit;
    });
    return base;
  });

  // کدام جای‌خالی الان «فعال» است
  const [activeBlankKey, setActiveBlankKey] = useState(null);

  // وقتی blankDefs / initialAnswers عوض می‌شود، جواب‌ها را سینک کن
  useEffect(() => {
    setAnswers((prev) => {
      const next = {};
      blankKeys.forEach((k) => {
        next[k] =
          prev[k] ??
          (initialAnswers && typeof initialAnswers[k] === "string"
            ? initialAnswers[k]
            : "");
      });
      return next;
    });
  }, [blankKeys, initialAnswers]);

  // اطلاع به والد
  useEffect(() => {
    onAnswerChange?.(answers);
  }, [answers, onAnswerChange]);

  // کلمات استفاده‌شده
  const usedBankKeys = useMemo(() => {
    const s = new Set();
    blankKeys.forEach((bk) => {
      const v = answers[bk];
      if (v) s.add(v);
    });
    return s;
  }, [answers, blankKeys]);

  /* ---------- handlers ---------- */

  const handleBlankClick = (blankKey) => {
    const current = answers[blankKey];
    if (current) {
      // اگر پر است، کلیک → خالی‌اش کن
      setAnswers((prev) => ({ ...prev, [blankKey]: "" }));
      if (activeBlankKey === blankKey) setActiveBlankKey(null);
    } else {
      // خالی است → به عنوان blank فعال انتخابش کن
      setActiveBlankKey(blankKey);
    }
  };

  const handleBankClick = (bankKey) => {
    // اگر این کلمه الان در یک blank استفاده شده → کلیک یعنی برداشتن از آن blank
    let assignedBlank = null;
    for (const bk of blankKeys) {
      if (answers[bk] === bankKey) {
        assignedBlank = bk;
        break;
      }
    }
    if (assignedBlank) {
      setAnswers((prev) => ({ ...prev, [assignedBlank]: "" }));
      if (activeBlankKey === assignedBlank) setActiveBlankKey(null);
      return;
    }

    // در غیر این صورت، روی blank فعال یا اولین خالی بنشیند
    const targetBlank =
      (activeBlankKey && blankKeys.includes(activeBlankKey)
        ? activeBlankKey
        : blankKeys.find((bk) => !answers[bk])) || null;

    if (!targetBlank) return;

    setAnswers((prev) => ({ ...prev, [targetBlank]: bankKey }));
  };

  const renderBlankControl = (blankKey, key) => {
    const assignedKey = answers[blankKey] ?? "";
    const isActive = activeBlankKey === blankKey;
    const label = assignedKey
      ? bankMap.get(assignedKey) || assignedKey
      : t("word_bank.blank_placeholder", {
          key: blankKey,
          defaultValue: `(${blankKey})`,
        });

    return (
      <button
        type="button"
        key={key}
        onClick={() => handleBlankClick(blankKey)}
        className={[
          "mx-1 inline-flex items-center justify-center min-w-[80px] px-2 py-1 rounded-md border border-dashed text-xs",
          assignedKey
            ? "bg-indigo-50 border-indigo-400 text-gray-800"
            : isActive
            ? "bg-yellow-50 border-yellow-400 text-gray-700"
            : "bg-white border-gray-400 text-gray-500",
        ].join(" ")}
      >
        {label}
      </button>
    );
  };

  /* ---------- UI blocks ---------- */

  if (!textHtml) {
    return (
      <div className="text-sm text-red-500">
        {t("word_bank.no_text_error", "No text defined for Word Bank item.")}
      </div>
    );
  }

  const passageNode = (
    <div
      className="
        prose prose-sm max-w-none leading-7
        [&_table]:table-fixed [&_table]:w-full
        [&_th]:px-3 [&_th]:py-2 [&_td]:px-3 [&_td]:py-2
        [&_thead_th]:text-left
      "
      dir={isRtl ? "rtl" : "ltr"}
    >
      {renderHtmlWithWordBank(textHtml, blankDefs, renderBlankControl)}
    </div>
  );

  const bankNode = (
    <div className="mt-4">
      <div className="text-xs font-semibold text-gray-600 mb-1">
        {t("word_bank.bank_title", "Word Bank")}
      </div>
      {bankOptions.length ? (
        <div className="flex flex-wrap gap-2">
          {bankOptions.map((opt) => {
            const isUsed = usedBankKeys.has(opt.key);
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => handleBankClick(opt.key)}
                className={[
                  "inline-flex items-center rounded-full border px-3 py-1 text-xs transition",
                  isUsed
                    ? "bg-gray-200 border-gray-400 text-gray-600 line-through"
                    : "bg-white border-gray-300 text-gray-800 hover:bg-gray-50",
                ].join(" ")}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      ) : (
        <div className="text-xs text-gray-400">
          {t("word_bank.bank_empty", "No words in bank.")}
        </div>
      )}
    </div>
  );

  const stemNode =
    !hideStem && (item?.stem?.fa || item?.stem?.en) ? (
      <div
        dir={isRtl ? "rtl" : "ltr"}
        className="prose prose-sm max-w-none mb-3 text-gray-800"
        dangerouslySetInnerHTML={{ __html: stemHtml }}
      />
    ) : null;

  let content;
  if (layout === "left-right" || layout === "right-left") {
    const left = (
      <div className="space-y-3">
        {stemNode}
        {bankNode}
      </div>
    );
    const right = passageNode;
    const reverse = layout === "right-left";

    content = (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {reverse ? (
          <>
            <div>{right}</div>
            <div>{left}</div>
          </>
        ) : (
          <>
            <div>{left}</div>
            <div>{right}</div>
          </>
        )}
      </div>
    );
  } else if (layout === "bottom-top") {
    content = (
      <>
        {passageNode}
        {bankNode}
        {stemNode && <div className="mt-4">{stemNode}</div>}
      </>
    );
  } else {
    // top-bottom
    content = (
      <>
        {stemNode}
        {passageNode}
        {bankNode}
      </>
    );
  }

  return (
    <div className="rounded-lg bg-white p-4 max-w-5xl mx-auto">
      {!hideStem && typeof questionNumber === "number" && (
        <div className="text-xs text-gray-500 mb-2">
          {t("preview.question_n", "Question")} {questionNumber}
        </div>
      )}
      {content}
    </div>
  );
}
